import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

function FormField({ label, value, required }) {
  return (
    <div className="fp-field">
      <label className="fp-label">
        {label}
        {required && <span className="fp-required">*</span>}
      </label>
      <div className={`fp-value ${required && !value ? "fp-value-missing" : ""}`}>{value || "\u2014"}</div>
    </div>
  );
}

function FormSection({ title, icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="fp-section">
      <div className="fp-section-header" onClick={() => setOpen(!open)}>
        <span><span className="tab-icon">{icon}</span> {title}</span>
        <span>{open ? "\u25be" : "\u25b8"}</span>
      </div>
      {open && <div className="fp-section-body">{children}</div>}
    </div>
  );
}

function FalconPayloadTab({ messageId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // --- Reprocess state ---
  const [reprocessState, setReprocessState] = useState("idle");
  const [reprocessResult, setReprocessResult] = useState(null);

  // --- Reprocess from Classifier state ---
  const [classifierState, setClassifierState] = useState("idle"); // idle | submitting | done | error
  const [classifierResult, setClassifierResult] = useState(null);

  // Cancellation ref — set to true when messageId changes or component unmounts
  const pollAbortRef = useRef(false);

  const pollStatus = useCallback(async (mid) => {
    const MAX_POLLS = 30; // 30 × 3s = 90s max wait
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      // Stop if the user switched to a different message or unmounted
      if (pollAbortRef.current) return;
      try {
        const { data: s } = await axios.get(
          `${API}/api/reprocess-falcon/${encodeURIComponent(mid)}/status`,
          { withCredentials: true }
        );
        if (pollAbortRef.current) return;
        if (s.done) {
          setReprocessResult(s);
          setReprocessState(s.quote_id ? "done" : "error");
          return;
        }
      } catch {}
    }
    if (!pollAbortRef.current) {
      setReprocessState("error");
      setReprocessResult({ error: "Timed out waiting for reprocess to complete" });
    }
  }, []);

  const handleReprocess = async () => {
    if (!messageId) return;
    setReprocessState("submitting");
    setReprocessResult(null);
    pollAbortRef.current = false;
    try {
      const { data: result } = await axios.post(
        `${API}/api/reprocess-falcon/${encodeURIComponent(messageId)}`,
        {},
        { withCredentials: true }
      );
      if (result.status === "already_processed") {
        setReprocessResult(result);
        setReprocessState("done");
        return;
      }
      // Already reprocessing (not stale) — just start polling, do not re-queue
      if (result.status === "reprocessing") {
        setReprocessResult({ message: result.message });
        setReprocessState("polling");
        pollStatus(messageId);
        return;
      }
      if (result.status === "blocked") {
        setReprocessResult({ error: result.error });
        setReprocessState("error");
        return;
      }
      setReprocessState("polling");
      pollStatus(messageId);
    } catch (err) {
      setReprocessResult({ error: err.response?.data?.error || "Request failed" });
      setReprocessState("error");
    }
  };

  const handleReprocessFromClassifier = async () => {
    if (!messageId) return;
    setClassifierState("submitting");
    setClassifierResult(null);
    try {
      const { data: result } = await axios.post(
        `${API}/api/reprocess/classifier/${encodeURIComponent(messageId)}`,
        {},
        { withCredentials: true }
      );
      setClassifierResult(result);
      setClassifierState("done");
    } catch (err) {
      setClassifierResult({ error: err.response?.data?.error || "Request failed" });
      setClassifierState("error");
    }
  };

  useEffect(() => {
    if (!messageId) return;
    // Cancel any in-flight poll from the previous message
    pollAbortRef.current = true;
    setLoading(true);
    setError("");
    setReprocessState("idle");
    setReprocessResult(null);
    setClassifierState("idle");
    setClassifierResult(null);
    axios.get(`${API}/api/falcon-payload/${encodeURIComponent(messageId)}`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load"))
      .finally(() => setLoading(false));
    return () => { pollAbortRef.current = true; };
  }, [messageId]);

  if (loading) return <div className="mm-empty">Loading...</div>;

  const broker = data?.broker_info || {};
  const insured = data?.insuredInfo || {};
  const gen = data?.genInfo || {};
  const uw = data?.underwriterInfo || {};
  const mailing = data?.gl_mailing_address || {};
  const locations = data?.gl_locations?.locations || [];
  const falconMeta = data?._falcon_meta || {};

  // Helper: coerce numeric/zero values to string so FormField renders them correctly
  const str = (v) => (v !== null && v !== undefined && v !== "") ? String(v) : null;

  const hasQuote = !!(falconMeta.quote_id || (reprocessResult?.quote_id));
  const btnLabel = {
    idle: "⚡ Retry Falcon Push",
    submitting: "Submitting...",
    polling: "Processing...",
    done: reprocessResult?.status === "already_processed" ? "✓ Already Processed" : "✓ Reprocessed",
    error: "✕ Failed — Retry",
  }[reprocessState];

  return (
    <div className="fp-container">
      {falconMeta.duplicate_found && falconMeta.duplicate_quote_id && (
        <div className="fp-dup-banner">
          🔄 Duplicate submission — Original Quote: <strong>#{falconMeta.duplicate_quote_id}</strong>
          {falconMeta.quote_id && <span> &nbsp;|&nbsp; New (declined) Quote: <strong>#{falconMeta.quote_id}</strong></span>}
        </div>
      )}

      {/* Reprocess Banner */}
      <div className="fp-reprocess-bar">
        <div className="fp-reprocess-info">
          {hasQuote ? (
            <span className="fp-reprocess-quote">
              ✓ Quote #{falconMeta.quote_id || reprocessResult?.quote_id} exists
            </span>
          ) : (
            <span className="fp-reprocess-hint">No quote created yet — push to Falcon manually</span>
          )}
        </div>
        <button
          className={`fp-reprocess-btn fp-reprocess-${classifierState}`}
          onClick={handleReprocessFromClassifier}
          disabled={classifierState === "submitting" || classifierState === "done"}
        >
          {classifierState === "submitting" && <span className="fp-reprocess-spinner" />}
          {{
            idle:       "🔄 Reprocess",
            submitting: "Queuing...",
            done:       "✓ Queued",
            error:      "✕ Failed — Retry",
          }[classifierState]}
        </button>
      </div>

      {/* Reprocess result detail */}
      {reprocessResult && reprocessState !== "idle" && (
        <div className={`fp-reprocess-result fp-reprocess-result-${reprocessResult.error ? "err" : "ok"}`}>
          {reprocessResult.error && <span>✕ {reprocessResult.error}</span>}
          {!reprocessResult.error && reprocessResult.status === "already_processed" && (
            <span>{reprocessResult.message}</span>
          )}
          {!reprocessResult.error && reprocessResult.status === "queued" && reprocessState === "polling" && (
            <span>Message queued — waiting for saga-engine to process...</span>
          )}
          {!reprocessResult.error && reprocessResult.quote_id && reprocessState === "done" && reprocessResult.status !== "already_processed" && (
            <span>✓ Quote #{reprocessResult.quote_id} created successfully</span>
          )}
        </div>
      )}
      {/* Classifier reprocess result */}
      {classifierResult && classifierState !== "idle" && (
        <div className={`fp-reprocess-result fp-reprocess-result-${classifierResult.error ? "err" : "ok"}`}>
          {classifierResult.error
            ? <span>✕ {classifierResult.error}</span>
            : <span>✓ Queued for reprocessing from classifier — message_id={messageId?.slice(0, 40)}...</span>
          }
        </div>
      )}

      {error && <div className="mm-empty" style={{ color: "var(--danger)" }}>{error}</div>}
      {!data && !error && <div className="mm-empty">No submission info found for this message.</div>}

      {data && (<>
      <div className="fp-meta">
        <span>Schema: {data.schema_version || "\u2014"}</span>
        <span>Run: {data.run_id || "\u2014"}</span>
        <span>Created: {data.created_at ? new Date(data.created_at).toLocaleString() : "\u2014"}</span>
      </div>

      <FormSection title="General Info" icon="📄">
        <div className="fp-grid">
          <FormField label="Policy Type" value={str(gen.policytype)} required />
          <FormField label="Bill Type" value={str(gen.billtype)} required />
          <FormField label="Effective Date" value={str(gen.effective_date)} required />
          <FormField label="Expiration Date" value={str(gen.expiration_date)} required />
          <FormField label="Lock Rate Date" value={str(gen.lock_rate_effective_date)} required />
          <FormField label="Application Type" value={Array.isArray(gen.application_type_id) ? gen.application_type_id.join(", ") : str(gen.application_type_id)} required />
          <FormField label="Business Years Exp" value={str(gen.business_yrs_exp)} required />
        </div>
      </FormSection>

      <FormSection title="Insured Info" icon="🏢">
        <div className="fp-grid">
          <FormField label="Insured Name" value={str(insured.insured_name)} required />
          <FormField label="DBA" value={str(insured.insured_dba)} />
          <FormField label="Entity Type" value={str(insured.entity_type)} required />
          <FormField label="Email" value={str(insured.insured_email)} />
          <FormField label="Phone" value={str(insured.insured_phone)} />
          <FormField label="Description of Operation" value={str(insured.description_of_operation)} required />
        </div>
      </FormSection>

      <FormSection title="Broker Info" icon="🤝">
        <div className="fp-grid">
          <FormField label="Agency Name" value={str(broker.agencyname)} required />
          <FormField label="Agency User ID" value={str(broker.agencyuserid)} required />
          <FormField label="Agency Phone" value={str(broker.agencyPhone)} />
          <FormField label="Agency Address" value={str(broker.agency_address)} />
          <FormField label="Agency City" value={str(broker.agency_city)} />
          <FormField label="Agency State" value={str(broker.agency_state)} />
          <FormField label="Default Email" value={str(broker.broker_default_email)} />
          <FormField label="Default Phone" value={str(broker.broker_default_phone)} />
        </div>
      </FormSection>

      <FormSection title="Underwriter Info" icon="👤">
        <div className="fp-grid">
          <FormField label="Current UW" value={str(uw.current_underwriter)} required />
          <FormField label="Original UW" value={str(uw.original_underwriter)} />
          <FormField label="Renewal UW" value={str(uw.renewal_underwriter)} />
        </div>
      </FormSection>

      <FormSection title="Mailing Address" icon="📨">
        <div className="fp-grid">
          <FormField label="Address" value={str(mailing.mailing_address)} required />
          <FormField label="City" value={str(mailing.mailing_city)} required />
          <FormField label="State" value={str(mailing.mailing_state)} required />
          <FormField label="Zip" value={str(mailing.mailing_zip)} required />
          <FormField label="Country" value={str(mailing.mailing_country)} />
        </div>
      </FormSection>

      <FormSection title={`Locations (${locations.length})`} icon="📍">
        {locations.map((loc, i) => (
            <div key={i} className="fp-location">
              <span className="fp-location-badge">Location {i + 1}</span>
              <div className="fp-grid">
                <FormField label="Address" value={str(loc.address)} required />
                <FormField label="City" value={str(loc.city)} required />
                <FormField label="State" value={str(loc.state)} required />
                <FormField label="Zip" value={str(loc.zip)} required />
                <FormField label="County" value={str(loc.county)} />
                <FormField label="Location Description" value={str(loc.location_description)} />
              </div>
            </div>
        ))}
        {locations.length === 0 && <div className="fp-empty">No locations</div>}
      </FormSection>
      </>)}
    </div>
  );
}

export default FalconPayloadTab;
